import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, X, CheckCircle, Layers, AlertCircle, Check } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo requirements
const LOGO_MIN_SIZE = 100; // Minimum 100x100 pixels
const LOGO_MAX_SIZE = 512; // Maximum 512x512 pixels
const LOGO_REQUIREMENTS = `Logo must be between ${LOGO_MIN_SIZE}x${LOGO_MIN_SIZE} and ${LOGO_MAX_SIZE}x${LOGO_MAX_SIZE} pixels (square recommended)`;

const categories = [
  { name: 'Marketing Automation', slug: 'marketing-automation' },
  { name: 'CRM Software', slug: 'crm-software' },
  { name: 'Project Management', slug: 'project-management' },
  { name: 'AI & Machine Learning', slug: 'ai-ml' },
  { name: 'Email Marketing', slug: 'email-marketing' },
  { name: 'HR Management', slug: 'hr-management' },
  { name: 'SEO Tools', slug: 'seo-tools' },
  { name: 'Analytics & BI', slug: 'analytics-bi' },
];

const SubmitTool = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    logo: '',
    description: '',
    category: '',
    categorySlug: '',
    pricing: '',
    priceType: 'freemium',
    features: [],
    website_url: '',
    submitter_name: '',
    submitter_email: '',
  });

  const [featureInput, setFeatureInput] = useState('');
  
  // Logo validation state
  const [logoValidation, setLogoValidation] = useState({
    checking: false,
    valid: null,
    error: '',
    dimensions: null
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const validateLogo = (url) => {
    if (!url) {
      setLogoValidation({ checking: false, valid: null, error: '', dimensions: null });
      return;
    }

    setLogoValidation({ checking: true, valid: null, error: '', dimensions: null });

    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      
      if (width < LOGO_MIN_SIZE || height < LOGO_MIN_SIZE) {
        setLogoValidation({
          checking: false,
          valid: false,
          error: `Logo is too small (${width}x${height}px). Minimum size is ${LOGO_MIN_SIZE}x${LOGO_MIN_SIZE}px.`,
          dimensions: { width, height }
        });
      } else if (width > LOGO_MAX_SIZE || height > LOGO_MAX_SIZE) {
        setLogoValidation({
          checking: false,
          valid: false,
          error: `Logo is too large (${width}x${height}px). Maximum size is ${LOGO_MAX_SIZE}x${LOGO_MAX_SIZE}px.`,
          dimensions: { width, height }
        });
      } else {
        setLogoValidation({
          checking: false,
          valid: true,
          error: '',
          dimensions: { width, height }
        });
      }
    };
    
    img.onerror = () => {
      setLogoValidation({
        checking: false,
        valid: false,
        error: 'Unable to load image. Please check the URL and try again.',
        dimensions: null
      });
    };

    img.src = url;
  };

  const handleLogoChange = (url) => {
    handleChange('logo', url);
    validateLogo(url);
  };

  const handleCategoryChange = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    if (category) {
      setFormData(prev => ({
        ...prev,
        category: category.name,
        categorySlug: category.slug
      }));
    }
  };

  const addFeature = () => {
    if (featureInput.trim() && formData.features.length < 6) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate logo before submission
    if (!logoValidation.valid) {
      if (logoValidation.error) {
        setError(`Logo Error: ${logoValidation.error}`);
      } else {
        setError('Please provide a valid logo URL that meets the size requirements.');
      }
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/submissions`, formData);
      setSubmissionId(response.data.id);
      setSubmitted(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit tool. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Submission Received!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for submitting <strong>{formData.name}</strong>. Our team will review 
                your submission and you'll be notified once it's approved.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Submission ID</p>
                <p className="font-mono text-sm text-gray-900">{submissionId}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild variant="outline">
                  <Link to="/">Back to Home</Link>
                </Button>
                <Button asChild className="bg-emerald-500 hover:bg-emerald-600">
                  <Link to="/tools">Browse Tools</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Submit Your Tool</h1>
                <p className="text-gray-500">Share your software with thousands of businesses</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tool Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tool Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tool Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., My Amazing Tool"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    placeholder="my-amazing-tool"
                    disabled
                  />
                  <p className="text-xs text-gray-400 mt-1">Auto-generated from name</p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe what your tool does and who it's for..."
                  rows={3}
                  required
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL *</label>
                <Input
                  value={formData.logo}
                  onChange={(e) => handleLogoChange(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  required
                  className={logoValidation.valid === false ? 'border-red-300 focus:ring-red-500' : logoValidation.valid === true ? 'border-emerald-300 focus:ring-emerald-500' : ''}
                />
                
                {/* Logo Requirements */}
                <div className="mt-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-500">{LOGO_REQUIREMENTS}</p>
                </div>

                {/* Logo Validation Status */}
                {logoValidation.checking && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking logo dimensions...
                  </div>
                )}

                {logoValidation.valid === false && logoValidation.error && (
                  <div className="mt-2 flex items-start gap-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{logoValidation.error}</span>
                  </div>
                )}

                {logoValidation.valid === true && logoValidation.dimensions && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span>Logo valid ({logoValidation.dimensions.width}x{logoValidation.dimensions.height}px)</span>
                  </div>
                )}

                {/* Logo Preview */}
                {formData.logo && logoValidation.valid && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg inline-block">
                    <p className="text-xs text-gray-500 mb-2">Preview:</p>
                    <img 
                      src={formData.logo} 
                      alt="Logo preview" 
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                <Input
                  value={formData.website_url}
                  onChange={(e) => handleChange('website_url', e.target.value)}
                  placeholder="https://yourtool.com"
                />
              </div>
            </div>

            {/* Category & Pricing */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Category & Pricing</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <Select value={formData.category} onValueChange={handleCategoryChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.slug} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pricing *</label>
                  <Input
                    value={formData.pricing}
                    onChange={(e) => handleChange('pricing', e.target.value)}
                    placeholder="e.g., Free - $99/mo"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Type</label>
                  <Select value={formData.priceType} onValueChange={(v) => handleChange('priceType', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="freemium">Freemium</SelectItem>
                      <SelectItem value="paid">Paid Only</SelectItem>
                      <SelectItem value="custom">Custom Pricing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Features</h2>
              <p className="text-sm text-gray-500 mb-4">Add up to 6 key features of your tool</p>
              
              <div className="flex gap-2 mb-4">
                <Input
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  placeholder="Enter a feature..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={addFeature}
                  disabled={formData.features.length >= 6}
                >
                  Add
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm"
                  >
                    {feature}
                    <button type="button" onClick={() => removeFeature(index)}>
                      <X className="w-4 h-4 hover:text-emerald-900" />
                    </button>
                  </span>
                ))}
                {formData.features.length === 0 && (
                  <p className="text-sm text-gray-400">No features added yet</p>
                )}
              </div>
            </div>

            {/* Submitter Information */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Information</h2>
              <p className="text-sm text-gray-500 mb-4">We'll contact you when your submission is reviewed</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <Input
                    value={formData.submitter_name}
                    onChange={(e) => handleChange('submitter_name', e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                  <Input
                    type="email"
                    value={formData.submitter_email}
                    onChange={(e) => handleChange('submitter_email', e.target.value)}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                disabled={submitting || logoValidation.valid === false || logoValidation.checking}
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Submit Tool</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubmitTool;
